import { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getApiBaseUrl } from "../../config/apiBase";
import { AuthenticatedFileLinks } from "../../components/AuthenticatedFileLinks";
import { useAppState } from "../../context/AppStateContext";
import {
  GST_PORTAL_HOME_URL,
  GST_SEARCH_TAXPAYER_URL,
  MCA_COMPANY_MASTER_URL,
} from "../../constants/govPortalLinks";
import { readFileAsRawBase64 } from "../../utils/readFileAsRawBase64";
import {
  canAccessSubcontractorStep,
  findSubcontractorMaster,
  isSubcontractorLinkageComplete,
} from "../../utils/subcontractor";

type SubcontractorTabProps = {
  /** Narrow layout on standalone “subcontractor to beneficiary” screen (no duplicate card title). */
  embedded?: "dedicated";
};

export function SubcontractorTab(props: SubcontractorTabProps = {}) {
  const { embedded } = props;
  const { id } = useParams<{ id: string }>();
  const { getById, subcontractorMasters, updateSubcontractor, finalizeSubcontractor } = useAppState();
  const app = id ? getById(id) : undefined;
  const [linkError, setLinkError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!app) return null;
  const link = app.subcontractor;
  const subGate = canAccessSubcontractorStep(app);
  const workOrderFileApiBase = `${getApiBaseUrl()}/api/applications/${encodeURIComponent(app.id)}/work-order-file`;

  const dedicated = embedded === "dedicated";

  return (
    <>
      {!dedicated ? (
        <p className="tab-intro">
          <strong>Subcontractor linkage.</strong> Firm identity, contact, and bank details are registered under{" "}
          <Link to="/subcontractor-onboarding">Subcontractor Onboarding</Link>. Here you only choose which registered firm is engaged
          for this beneficiary; you may optionally attach a work order or engagement letter for this unit. Use that screen first if the
          firm is not yet listed. If this section is locked, the grey box lists what is still missing upstream.{" "}
          <span className="text-muted">
            Official checks:{" "}
            <a href={GST_SEARCH_TAXPAYER_URL} target="_blank" rel="noopener noreferrer">
              GST taxpayer search
            </a>
            {" · "}
            <a href={GST_PORTAL_HOME_URL} target="_blank" rel="noopener noreferrer">
              GST home
            </a>
            {" · "}
            <a href={MCA_COMPANY_MASTER_URL} target="_blank" rel="noopener noreferrer">
              MCA company / LLP master
            </a>
            .
          </span>
        </p>
      ) : null}

      <div
        className={
          dedicated
            ? "card card--gov card--elevated card--subcontractor card--subcontractor-dedicated"
            : "card card--gov card--elevated card--subcontractor"
        }
      >
        {!dedicated ? <h2 className="card__title card__title--gov">Subcontractor linkage</h2> : null}

        {!subGate ? (
          <div className="panel-locked">
            <p className="panel-locked__title">This section is locked</p>
            <ul className="panel-locked__list">
              <li>Complete HO validation, Aadhaar seeding, and ICICI + MOU on the respective tabs.</li>
              <li>Resolve any active hold on the file.</li>
              <li>When Bills is enabled for your office, confirm seeding re-verification for each work-done bill there.</li>
            </ul>
          </div>
        ) : (
          <>
            {app.status === "subcontractor_linked" && link.linkedAt ? (
              <p className="alert alert--success">
                <strong>On record.</strong> Subcontractor linked at {link.linkedAt.slice(0, 16).replace("T", " ")}. You may
                change the selected firm or replace the work order; clearing the selected firm will remove linked status until you save again.
              </p>
            ) : null}
            {linkError ? (
              <p className="alert alert--danger" role="alert">
                {linkError}
              </p>
            ) : null}

            <h3 className="form-section-title">Registered subcontractor</h3>
            <div className="field field--full">
              <label htmlFor="sub-master-select">Registered firm</label>
              <select
                id="sub-master-select"
                value={link.subcontractorMasterId ?? ""}
                onChange={(e) => {
                  setLinkError(null);
                  const v = e.target.value;
                  void updateSubcontractor(app.id, {
                    subcontractorMasterId: v === "" ? null : v,
                  });
                }}
              >
                <option value="">— Select an onboarded firm —</option>
                {subcontractorMasters.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.firmName}
                    {m.contactPhone ? ` · ${m.contactPhone}` : ""}
                  </option>
                ))}
              </select>
              <p className="text-muted" style={{ marginTop: "0.35rem", fontSize: "0.8125rem" }}>
                Missing a firm?{" "}
                <Link to="/subcontractor-onboarding" className="btn btn--link btn--sm" style={{ padding: 0, verticalAlign: "baseline" }}>
                  Open Subcontractor Onboarding
                </Link>{" "}
                to register it, then return here.
              </p>
            </div>

            <h3 className="form-section-title">Engagement for this beneficiary</h3>
            <div className="field field--full">
              <label>Work order / engagement letter (scan)</label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => {
                  setLinkError(null);
                  const f = e.target.files?.[0];
                  void updateSubcontractor(app.id, { workOrderFileName: f?.name ?? null });
                }}
              />
              {link.workOrderFileName ? (
                <span className="text-muted file-hint">On file: {link.workOrderFileName}</span>
              ) : null}
              {link.workOrderFileStored ? (
                <div className="text-muted file-hint" style={{ marginTop: "0.5rem" }}>
                  <AuthenticatedFileLinks
                    fileUrlBase={workOrderFileApiBase}
                    fallbackFileName={link.workOrderFileName ?? "work-order"}
                  />
                </div>
              ) : null}
            </div>

            {link.workOrderFileStored ? (
              <p className="text-muted section-lead section-lead--tight">
                Work order is stored on the server. Use Download or View above, or choose a new file to replace it. Firm
                details stay under Subcontractor Onboarding.
              </p>
            ) : (
              <p className="section-lead section-lead--tight">
                Required: a firm selected from the onboarded list (firm must have full KYC on file — Aadhaar, bank, and
                documents — under Subcontractor Onboarding). Work order upload is optional.
              </p>
            )}
            <div className="toolbar-row">
              <button
                type="button"
                className="btn btn--primary btn--tg btn--lg"
                onClick={() => {
                  void (async () => {
                    setLinkError(null);
                    const master = findSubcontractorMaster(subcontractorMasters, link.subcontractorMasterId);
                    let filePayload: { workOrderFileBase64: string; workOrderMimeType: string | null } | undefined;
                    const f = fileInputRef.current?.files?.[0];
                    if (f) {
                      try {
                        const r = await readFileAsRawBase64(f);
                        filePayload = { workOrderFileBase64: r.base64, workOrderMimeType: r.mimeType };
                      } catch {
                        setLinkError("Could not read the selected file.");
                        return;
                      }
                    }
                    const effective = filePayload ? { ...link, ...filePayload } : link;
                    if (!isSubcontractorLinkageComplete(effective, master)) {
                      setLinkError(
                        "Choose a subcontractor from the onboarded list. Register the firm under Subcontractor Onboarding first if it is not listed.",
                      );
                      return;
                    }
                    await finalizeSubcontractor(app.id, filePayload);
                  })();
                }}
              >
                {app.status === "subcontractor_linked" ? "Update subcontractor linkage" : "Record subcontractor against beneficiary"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
